import React, { useState, useEffect, useRef } from 'react';

// Track container width so the SVG renders 1:1 instead of stretching.
function useContainerWidth(initial = 600) {
  const ref = useRef(null);
  const [width, setWidth] = useState(initial);
  useEffect(() => {
    if (!ref.current || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect?.width;
      if (w && w > 0) setWidth(Math.round(w));
    });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

// ── Area + line chart for revenue over time ────────────────────────
export function AreaChart({ data, height = 200, formatY = v => v, color = 'var(--blue)' }) {
  const [hover, setHover] = useState(null);
  const [containerRef, width] = useContainerWidth(600);
  if (!data || data.length === 0) return <Empty>No data yet</Empty>;

  const padding = { left: 44, right: 12, top: 12, bottom: 28 };
  const cw = Math.max(width - padding.left - padding.right, 1);
  const ch = height - padding.top - padding.bottom;

  const max = Math.max(...data.map(d => d.value), 1);
  const niceMax = niceCeil(max);
  const xStep = data.length > 1 ? cw / (data.length - 1) : 0;

  const points = data.map((d, i) => ({
    x: padding.left + i * xStep,
    y: padding.top + ch - (d.value / niceMax) * ch,
    raw: d,
  }));

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
  const areaPath = `${linePath} L${points[points.length - 1].x},${padding.top + ch} L${points[0].x},${padding.top + ch} Z`;

  const ticks = [0, 0.25, 0.5, 0.75, 1].map(t => ({
    y: padding.top + ch - t * ch,
    value: niceMax * t,
  }));

  // Unique gradient id so multiple AreaCharts on one page don't collide
  const gradId = React.useId ? `area-${React.useId()}` : 'area-grad';

  return (
    <div ref={containerRef} style={{ width: '100%' }}>
      <svg viewBox={`0 0 ${width} ${height}`} width={width} height={height} style={{ display: 'block' }}>
        {ticks.map((t, i) => (
          <g key={i}>
            <line x1={padding.left} x2={width - padding.right} y1={t.y} y2={t.y}
                  stroke="var(--border-light)" strokeWidth="1" />
            <text x={padding.left - 6} y={t.y + 3} fontSize="10" textAnchor="end" fill="var(--text-subtle)">
              {formatY(t.value)}
            </text>
          </g>
        ))}

        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor={color} stopOpacity="0.35" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill={`url(#${gradId})`} />
        <path d={linePath} fill="none" stroke={color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />

        {points.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r={hover === i ? 5 : 3}
                  fill={color} stroke="var(--card-bg)" strokeWidth="2"
                  onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}
                  style={{ cursor: 'pointer', transition: 'r 0.1s' }} />
        ))}

        {points.map((p, i) => (data.length <= 8 || i % 2 === 0) && (
          <text key={i} x={p.x} y={height - 8} fontSize="10" textAnchor="middle" fill="var(--text-subtle)">
            {p.raw.label}
          </text>
        ))}

        {hover != null && (() => {
          const p = points[hover];
          const text = `${p.raw.label}: ${formatY(p.raw.value)}`;
          const w = Math.max(80, text.length * 7);
          const tx = Math.min(Math.max(p.x - w / 2, padding.left), width - padding.right - w);
          return (
            <g>
              <rect x={tx} y={p.y - 32} width={w} height={22} rx={5}
                    fill="var(--text)" opacity="0.92" />
              <text x={tx + w / 2} y={p.y - 17} fontSize="11" fontWeight="600"
                    textAnchor="middle" fill="var(--card-bg)">
                {text}
              </text>
            </g>
          );
        })()}
      </svg>
    </div>
  );
}

// ── Vertical bars — for document counts per month ────────────────
export function BarChart({ data, height = 180, formatY = v => v, color = 'var(--blue)' }) {
  const [hover, setHover] = useState(null);
  const [containerRef, width] = useContainerWidth(600);
  if (!data || data.length === 0) return <Empty>No data yet</Empty>;

  const padding = { left: 36, right: 12, top: 12, bottom: 26 };
  const cw = Math.max(width - padding.left - padding.right, 1);
  const ch = height - padding.top - padding.bottom;

  const max = Math.max(...data.map(d => d.value), 1);
  const niceMax = niceCeil(max);
  const slot = cw / data.length;
  const barW = Math.max(Math.min(slot * 0.6, 28), 6);

  const ticks = [0, 0.5, 1].map(t => ({
    y: padding.top + ch - t * ch,
    value: niceMax * t,
  }));

  return (
    <div ref={containerRef} style={{ width: '100%' }}>
      <svg viewBox={`0 0 ${width} ${height}`} width={width} height={height} style={{ display: 'block' }}>
        {ticks.map((t, i) => (
          <g key={i}>
            <line x1={padding.left} x2={width - padding.right} y1={t.y} y2={t.y}
                  stroke="var(--border-light)" strokeWidth="1" />
            <text x={padding.left - 6} y={t.y + 3} fontSize="10" textAnchor="end" fill="var(--text-subtle)">
              {formatY(t.value)}
            </text>
          </g>
        ))}

        {data.map((d, i) => {
          const cx = padding.left + slot * (i + 0.5);
          const h = (d.value / niceMax) * ch;
          const y = padding.top + ch - h;
          return (
            <g key={i} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}
               style={{ cursor: 'pointer' }}>
              <rect x={cx - barW / 2} y={y} width={barW} height={Math.max(h, 0)}
                    fill={color} opacity={hover === i ? 1 : 0.85} rx={3} />
              <text x={cx} y={height - 8} fontSize="10" textAnchor="middle" fill="var(--text-subtle)">
                {d.label}
              </text>
            </g>
          );
        })}

        {hover != null && (() => {
          const d = data[hover];
          const cx = padding.left + slot * (hover + 0.5);
          const h = (d.value / niceMax) * ch;
          const y = padding.top + ch - h;
          const text = `${d.label}: ${formatY(d.value)}`;
          const w = Math.max(70, text.length * 7);
          const tx = Math.min(Math.max(cx - w / 2, padding.left), width - padding.right - w);
          return (
            <g>
              <rect x={tx} y={y - 28} width={w} height={20} rx={5}
                    fill="var(--text)" opacity="0.92" />
              <text x={tx + w / 2} y={y - 14} fontSize="11" fontWeight="600"
                    textAnchor="middle" fill="var(--card-bg)">
                {text}
              </text>
            </g>
          );
        })()}
      </svg>
    </div>
  );
}

// ── Stacked horizontal bar — A/R aging by bucket ──────────────────
export function StackedBar({ segments, formatValue = v => v, height = 28 }) {
  const total = segments.reduce((s, x) => s + x.value, 0);
  if (total <= 0) return <Empty>No outstanding amounts</Empty>;
  return (
    <div>
      <div style={{
        display: 'flex', height, borderRadius: 8, overflow: 'hidden',
        border: '1px solid var(--border-light)', background: 'var(--border-light)',
      }}>
        {segments.map(s => {
          const pct = (s.value / total) * 100;
          if (pct <= 0) return null;
          return (
            <div key={s.label} title={`${s.label}: ${formatValue(s.value)} (${pct.toFixed(0)}%)`}
                 style={{
                   width: `${pct}%`, background: s.color,
                   display: 'flex', alignItems: 'center', justifyContent: 'center',
                   fontSize: 11, fontWeight: 600, color: '#fff',
                   transition: 'width 0.3s',
                 }}>
              {pct >= 8 ? `${pct.toFixed(0)}%` : ''}
            </div>
          );
        })}
      </div>
      <ul style={{ listStyle: 'none', padding: 0, marginTop: 12, display: 'grid',
                   gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8, fontSize: 12 }}>
        {segments.map(s => (
          <li key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: s.color, flexShrink: 0 }} />
            <span style={{ flex: 1, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {s.label}{s.count != null && ` · ${s.count}`}
            </span>
            <span style={{ fontWeight: 600, color: 'var(--text)' }}>{formatValue(s.value)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Donut chart with center label + legend ────────────────────────
export function DonutChart({ data, size = 180, thickness = 28, centerLabel, centerValue }) {
  if (!data || data.length === 0 || data.every(d => d.value === 0)) return <Empty>No data yet</Empty>;

  const total = data.reduce((s, d) => s + d.value, 0);
  const r = (size - thickness) / 2;
  const c = size / 2;
  const circ = 2 * Math.PI * r;

  let offset = 0;
  const segments = data.map((d, i) => {
    const len = (d.value / total) * circ;
    const seg = {
      ...d,
      dasharray: `${len} ${circ - len}`,
      dashoffset: -offset,
    };
    offset += len;
    return seg;
  });

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap', justifyContent: 'center' }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}>
        {/* Background ring */}
        <circle cx={c} cy={c} r={r} fill="none" stroke="var(--border-light)" strokeWidth={thickness} />
        {/* Segments */}
        {segments.map((s, i) => (
          <circle key={i} cx={c} cy={c} r={r}
                  fill="none" stroke={s.color} strokeWidth={thickness}
                  strokeDasharray={s.dasharray} strokeDashoffset={s.dashoffset}
                  transform={`rotate(-90 ${c} ${c})`}
                  style={{ transition: 'stroke-dasharray 0.3s' }} />
        ))}
        {/* Center text */}
        <text x={c} y={c - 4} textAnchor="middle" fontSize="22" fontWeight="800" fill="var(--text)">
          {centerValue ?? total}
        </text>
        <text x={c} y={c + 16} textAnchor="middle" fontSize="11" fill="var(--text-subtle)" textTransform="uppercase">
          {centerLabel ?? 'Total'}
        </text>
      </svg>

      {/* Legend */}
      <ul style={{ listStyle: 'none', padding: 0, fontSize: 13, minWidth: 130 }}>
        {data.map(d => {
          const pct = total > 0 ? Math.round((d.value / total) * 100) : 0;
          return (
            <li key={d.label} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: d.color, flexShrink: 0 }} />
              <span style={{ flex: 1, color: 'var(--text-muted)' }}>{d.label}</span>
              <span style={{ fontWeight: 600, color: 'var(--text)' }}>{d.value}</span>
              <span style={{ fontSize: 11, color: 'var(--text-subtle)', minWidth: 32, textAlign: 'right' }}>{pct}%</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ── Horizontal bar chart for top clients / categories ────────────
export function HorizontalBars({ data, formatValue = v => v, color = 'var(--blue)', maxRows = 5 }) {
  if (!data || data.length === 0) return <Empty>No data yet</Empty>;
  const rows = data.slice(0, maxRows);
  const max = Math.max(...rows.map(r => r.value), 1);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {rows.map((r, i) => {
        const pct = (r.value / max) * 100;
        return (
          <div key={r.label + i}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
              <span style={{ color: 'var(--text)', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '70%' }}>
                {r.label}
              </span>
              <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>{formatValue(r.value)}</span>
            </div>
            <div style={{ height: 8, background: 'var(--border-light)', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{
                height: '100%', width: `${pct}%`, background: color,
                borderRadius: 4, transition: 'width 0.3s',
              }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── helpers ──────────────────────────────────────────────────────
function Empty({ children }) {
  return <div style={{ textAlign: 'center', padding: '40px 16px', fontSize: 13, color: 'var(--text-subtle)' }}>{children}</div>;
}

function niceCeil(n) {
  if (n <= 0) return 1;
  const exp = Math.floor(Math.log10(n));
  const base = Math.pow(10, exp);
  const m = n / base;
  const niceM = m <= 1 ? 1 : m <= 2 ? 2 : m <= 5 ? 5 : 10;
  return niceM * base;
}
